import { useState, useEffect } from "react";

import { Sparkles, User, PenLine, LayoutTemplate, Wand2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/sonner";
import { Textarea } from "@/components/ui/textarea";
import { customBoardSchema, validateFormData } from "@/lib/validation";

interface CustomBoardFormProps {
  problem: string;
  context: string;
  onProblemChange: (value: string) => void;
  onContextChange: (value: string) => void;
  onSubmit: (mode: 'manual' | 'automated') => void;
  isSubmitting: boolean;
  remixBoard?: { name: string } | null;
}

const CustomBoardForm = ({ 
  problem, 
  context, 
  onProblemChange, 
  onContextChange, 
  onSubmit,
  isSubmitting,
  remixBoard,
}: CustomBoardFormProps) => {
  const [refinementMode, setRefinementMode] = useState<'manual' | 'automated'>('manual');
  const [inputMode, setInputMode] = useState<'guided' | 'freeform'>('guided');
  
  // Guided mode state
  const [role, setRole] = useState("");
  const [goal, setGoal] = useState("");
  const [obstacle, setObstacle] = useState("");

  // Sync guided inputs to the main problem prop
  useEffect(() => {
    if (inputMode === 'guided') {
      // Only update if at least one field has content to avoid overwriting existing data on mount
      if (role || goal || obstacle) {
        const newProblem = `I am a ${role || "[Role]"} trying to ${goal || "[Goal]"}, but I am stuck on ${obstacle || "[Obstacle]"}.`;
        onProblemChange(newProblem);
      }
    }
  }, [role, goal, obstacle, inputMode]);

  const handleSubmit = () => {
    // Validate input using Zod schema
    const validation = validateFormData(customBoardSchema, { problem, context });
    
    if (!validation.success) {
      const firstError = Object.values((validation as { success: false; errors: Record<string, string> }).errors)[0];
      toast.error(firstError);
      return;
    }
    onSubmit(refinementMode);
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-semibold">
          {remixBoard ? `Remixing: ${remixBoard.name}` : "Build a Custom Board"}
        </h1>
        <p className="text-muted-foreground">
          {remixBoard 
            ? "Tell us how you want to adapt this template to your specific needs"
            : "Tell us about your challenge and we'll suggest the right experts"}
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                {remixBoard && <Wand2 className="h-5 w-5 text-primary" />}
                {remixBoard ? "Adaptation Goals" : "Your Challenge"}
              </CardTitle>
              <CardDescription>
                {remixBoard 
                  ? "How should we modify this board for you?" 
                  : "Help us understand what you're trying to solve"}
              </CardDescription>
            </div>
            <Tabs value={inputMode} onValueChange={(v) => setInputMode(v as 'guided' | 'freeform')} className="w-[200px]">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="guided">
                  <LayoutTemplate className="h-4 w-4 mr-2" />
                  Guided
                </TabsTrigger>
                <TabsTrigger value="freeform">
                  <PenLine className="h-4 w-4 mr-2" />
                  Freeform
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {inputMode === 'guided' ? (
            <div className="space-y-4 animate-fade-in">
              <div className="grid gap-4 p-4 border rounded-lg bg-muted/30">
                <div className="space-y-2">
                  <Label htmlFor="role" className="text-primary font-semibold">I am a... (e.g. Product Manager, Solo Founder, Marketing Lead)</Label>
                  <Input
                    id="role"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    placeholder="Your role or business type"
                    className="bg-background"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="goal" className="text-primary font-semibold">Trying to... (e.g. launch a new SaaS product, scale my agency)</Label>
                  <Input
                    id="goal"
                    value={goal}
                    onChange={(e) => setGoal(e.target.value)}
                    placeholder="Your primary goal or objective"
                    className="bg-background"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="obstacle" className="text-primary font-semibold">But I am stuck on... (e.g. pricing strategy, finding the right niche)</Label>
                  <Input
                    id="obstacle"
                    value={obstacle}
                    onChange={(e) => setObstacle(e.target.value)}
                    placeholder="The specific challenge or obstacle you're facing"
                    className="bg-background"
                  />
                </div>
              </div>
              <p className="text-sm text-muted-foreground italic">
                Preview: "I am a {role || "..."} trying to {goal || "..."}, but I am stuck on {obstacle || "..."}."
              </p>
            </div>
          ) : (
            <div className="space-y-4 animate-fade-in">
              <div>
                <Label htmlFor="problem">What is the core problem you are trying to solve?</Label>
                <Input
                  id="problem"
                  value={problem}
                  onChange={(e) => onProblemChange(e.target.value)}
                  placeholder="e.g., I need to launch my first online course but don't know where to start"
                />
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="context">Additional context, goals, or constraints (Optional)</Label>
            <Textarea
              id="context"
              value={context}
              onChange={(e) => onContextChange(e.target.value)}
              placeholder="Share any relevant details about your business, audience, budget, timeline, etc."
              rows={4}
              className="mt-2"
            />
          </div>

          <div className="space-y-3 pt-4 border-t">
            <Label>Generation Mode</Label>
            <RadioGroup value={refinementMode} onValueChange={(v: any) => setRefinementMode(v)}>
              <div className="flex items-start space-x-2 rounded-lg border p-3 has-[:checked]:border-primary">
                <RadioGroupItem value="manual" id="manual" />
                <div className="flex-1">
                  <Label htmlFor="manual" className="font-medium cursor-pointer flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Interactive Refinement
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Review and refine the board details and personas as they are generated. You guide the AI at each step.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-2 rounded-lg border p-3 has-[:checked]:border-primary">
                <RadioGroupItem value="automated" id="automated" />
                <div className="flex-1">
                  <Label htmlFor="automated" className="font-medium cursor-pointer flex items-center gap-2">
                    <Sparkles className="h-4 w-4" />
                    Fully Automated
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    AI generates the board name, description, and all persona details in one go. Faster, less intervention.
                  </p>
                </div>
              </div>
            </RadioGroup>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="w-full"
          >
            {isSubmitting ? "Generating..." : "Next: Generate Board"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomBoardForm;
